import React, { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import "../styles/intelligence.scss";

gsap.registerPlugin(ScrollTrigger);

const CASE_STUDY_CARDS = [
  {
    id: 1,
    title: "Power Procurement Optimisation",
    image: "/image/Intelligence-img1.jpg",
    bgImage: "/image/Intelligence-bg1.jpg",
    challenge: "Volatile short-term power prices and manual procurement decisions limiting cost efficiency.",
    solution: "AI-assisted market intelligence and automated bidding across DAM, RTM, and G-DAM.",
    result: "Up to 10% reduction in power procurement costs with improved sourcing control and reduced volatility exposure."
  },
  {
    id: 2,
    title: "Real-time Load Management",
    image: "/image/Intelligence-img2.jpg",
    bgImage: "/image/Intelligence-bg2.jpg",
    challenge: "Inability to monitor energy consumption in real-time leading to inefficient peak demand management.",
    solution: "IoT-enabled smart metering with real-time dashboard and predictive analytics.",
    result: "15% reduction in peak demand charges and 20% improvement in load forecasting accuracy."
  },
  {
    id: 3,
    title: "Renewable Energy Integration",
    image: "/image/Intelligence-img1.jpg",
    bgImage: "/image/Intelligence-bg1.jpg",
    challenge: "Difficulty integrating renewable energy sources with variable generation patterns.",
    solution: "Machine learning algorithms for forecasting and automated dispatch optimization.",
    result: "Increased renewable penetration by 35% with improved grid stability and reduced curtailment."
  },
  {
    id: 4,
    title: "Energy Data Consolidation",
    image: "/image/Intelligence-img2.jpg",
    bgImage: "/image/Intelligence-bg2.jpg",
    challenge: "Fragmented energy data across multiple systems and facilities.",
    solution: "Unified cloud-based analytics platform with automated data aggregation.",
    result: "Single source of truth established, reducing reporting time by 80% and enabling faster decision-making."
  }
];

export default function ProvenIntelligence() {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const bgImagesRef = useRef([]);


  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const cards = containerRef.current;
      const header = document.querySelector(".header");
      if (!cards) return;

      const numCards = CASE_STUDY_CARDS.length;

      // Calculate total distance including gap at the end for last card
      const computedStyle = window.getComputedStyle(cards);
      const gap = parseFloat(computedStyle.gap);

      // scrollMultiplier = 1.5 gives a comfortable scroll pace —
      // enough scroll depth so each card transition feels deliberate,
      // without being so long the user feels stuck.
      const scrollMultiplier = 1.5;

      // Sync background images with active cards
      gsap.set(bgImagesRef.current, { opacity: 0 });
      gsap.set(bgImagesRef.current[0], { opacity: 1 });

      let currentIndex = 0;

      const getScrollAmount = () => {
        // Total horizontal distance to translate the container so the last card
        // is fully visible with its right padding cleared.
        // cards.scrollWidth = padding-left + sum(card-widths) + sum(gaps) + padding-right
        // We translate by exactly (scrollWidth - viewport width) so the last
        // card's right edge lands at the viewport's right edge.
        return cards.scrollWidth - window.innerWidth;
      };

      // Animate cards horizontally
      //   gsap.to(cards, {
      //     x: -totalDistance,
      //     ease: "none",
      //     scrollTrigger: {
      //       trigger: sectionRef.current,
      //       start: "top top",
      //       end: `+=${totalDistance * scrollMultiplier}`,
      //       scrub: 1,
      //       pin: true,
      //       invalidateOnRefresh: true,
      //     },
      //   });

      //   ScrollTrigger.create({
      //     trigger: sectionRef.current,
      //     start: "top top",
      //     end: `+=${totalDistance * scrollMultiplier}`,
      //     scrub: 1,

      //     onUpdate: (self) => {
      //       const progress = self.progress; // 0 → 1
      //       const newIndex = Math.round(progress * (numCards - 1));

      //       if (newIndex !== currentIndex) {
      //         // Fade out old background
      //         gsap.to(bgImagesRef.current[currentIndex], {
      //           opacity: 0,
      //           duration: 0.3,
      //           overwrite: true,
      //         });

      //         // Fade in new background
      //         gsap.to(bgImagesRef.current[newIndex], {
      //           opacity: 1,
      //           duration: 0.3,
      //           overwrite: true,
      //         });

      //         currentIndex = newIndex;
      //       }
      //     },
      //   });
      // });

      // 2. UNIFIED SCROLL TRIGGER
      gsap.to(cards, {
        // Use a function `() => ...` so GSAP evaluates this on every resize
        x: () => -getScrollAmount(),
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          // Use a function here too for dynamic ending points
          end: () => `+=${getScrollAmount() * scrollMultiplier}`,
          scrub: 1,
          pin: true,
          invalidateOnRefresh: true, // Forces GSAP to re-run getScrollAmount() on resize

          // to show/hide header
          // onEnter: () => {
          //   gsap.to(header, { y: "-100%", duration: 0.3 });
          // },

          // onEnterBack: () => {
          //   gsap.to(header, { y: "-100%", duration: 0.3 });
          // },

          // onLeave: () => {
          //   gsap.to(header, { y: "0%", duration: 0.3 });
          // },

          // onLeaveBack: () => {
          //   gsap.to(header, { y: "0%", duration: 0.3 });
          // },

          // Handle background switching natively inside the same trigger
          onUpdate: (self) => {
            const progress = self.progress;
            const newIndex = Math.round(progress * (numCards - 1));
          

            if (newIndex !== currentIndex) {
              // Fade out old background
              gsap.to(bgImagesRef.current[currentIndex], {
                opacity: 0,
                duration: 0.4,
                overwrite: "auto",
              });

              // Fade in new background
              gsap.to(bgImagesRef.current[newIndex], {
                opacity: 1,
                duration: 0.4,
                overwrite: "auto",
              });

              currentIndex = newIndex;
            }
          },
        },
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <section className="proven-intelligence" ref={sectionRef}>
      <div className="proven-intelligence-bg">
        {CASE_STUDY_CARDS.map((card, index) => (
          <img
            key={`bg-${card.id}`}
            ref={(el) => (bgImagesRef.current[index] = el)}
            src={card.bgImage}
            alt=""
            className="bg-image"
          />
        ))}
      </div>

      <div className="cards-container" ref={containerRef}>
        {CASE_STUDY_CARDS.map((card) => (
          <div key={card.id} className="carousel-card">
            <div className="card-image">
              <img src={card.image} alt={card.title} />
            </div>
            <div className="card-content">
              <h2>{card.title}</h2>
              <div className="card-section">
                <p className="section-text"><strong>Challenge: </strong>{card.challenge}</p>
                <p className="section-text"><strong>Solution: </strong>{card.solution}</p>
                <p className="section-text"><strong>Result: </strong>{card.result}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}